/**
 * Auth session generation guard — invalidates authenticated async work on logout.
 * Public landing must not wait on network/profile/friend-request cleanup.
 */

export const AUTH_REMOTE_SIGNOUT_TIMEOUT_MS = 3_000;
export const AUTH_BOOTSTRAP_PROBE_MS = 6_000;

/** Explicit auth resolution — do not infer logout from `user === null` alone. */
export type AuthResolution = 'pending' | 'authenticated' | 'logged_out';

export type AuthLogoutLogEvent =
  | 'AUTH_LOGOUT_START'
  | 'AUTH_GENERATION_INVALIDATED'
  | 'AUTH_LOCAL_STATE_CLEARED'
  | 'AUTH_PUBLIC_ROUTE_RENDERED'
  | 'AUTH_REMOTE_SIGNOUT_DONE'
  | 'AUTH_LOGOUT_DONE'
  | 'STALE_AUTH_RESULT_IGNORED'
  | 'AUTH_LOGOUT_ERROR';

export type FriendRequestLogEvent =
  | 'FRIEND_REQUEST_START'
  | 'FRIEND_REQUEST_CREATED'
  | 'FRIEND_REQUEST_REFRESH_START'
  | 'FRIEND_REQUEST_DONE';

export function logAuthEvent(
  event: AuthLogoutLogEvent,
  detail?: Record<string, unknown>
): void {
  if (detail) console.log(event, detail);
  else console.log(event);
}

export function logFriendRequestEvent(
  event: FriendRequestLogEvent,
  detail?: Record<string, unknown>
): void {
  if (detail) console.log(event, detail);
  else console.log(event);
}

export interface AuthSessionGuard {
  /** Current authenticated generation. Bumped when logout begins. */
  getGeneration: () => number;
  /** True if `generation` still matches the active generation. */
  isActive: (generation: number) => boolean;
  /** Snapshot generation for an authenticated async run. */
  beginAuthenticatedWork: () => number;
  /**
   * Begin logout: bump generation so all prior authenticated work becomes stale.
   * Returns the new (post-logout) generation. Idempotent while logoutInFlight.
   */
  beginLogout: () => { generation: number; alreadyInFlight: boolean };
  endLogout: () => void;
  isLogoutInFlight: () => boolean;
}

export function createAuthSessionGuard(): AuthSessionGuard {
  let generation = 0;
  let logoutInFlight = false;

  return {
    getGeneration: () => generation,
    isActive: (g: number) => g === generation,
    beginAuthenticatedWork: () => generation,
    beginLogout: () => {
      if (logoutInFlight) {
        return { generation, alreadyInFlight: true };
      }
      logoutInFlight = true;
      generation += 1;
      logAuthEvent('AUTH_GENERATION_INVALIDATED', { generation });
      return { generation, alreadyInFlight: false };
    },
    endLogout: () => {
      logoutInFlight = false;
    },
    isLogoutInFlight: () => logoutInFlight,
  };
}

/** Race a promise; does not cancel underlying work — caller must check generation. */
export function raceWithTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label = 'Timeout'
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(label)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/**
 * Splash / route precedence helper (pure).
 *
 * - `pending`: unknown auth (cold start) → bounded bootstrap splash
 * - `logged_out`: local logout terminal OR confirmed no session → public immediately
 * - `authenticated`: may show auth-data splash while profile/network loads
 *
 * Never treat `user === null` alone as bootstrap-loading.
 */
export function resolveAuthRouteDecision(opts: {
  authResolution: AuthResolution;
  authProbeResolved: boolean;
  isAuthBypassRoute: boolean;
  hasUser: boolean;
  authenticatedDataLoading?: boolean;
}): 'bootstrap-splash' | 'public' | 'auth-data-splash' | 'authenticated' {
  if (opts.isAuthBypassRoute) {
    return opts.hasUser ? 'authenticated' : 'public';
  }

  // Local logout terminal OR confirmed anonymous — never wait on isReady/network.
  if (opts.authResolution === 'logged_out') {
    return 'public';
  }

  // Cold start / hydration still unresolved
  if (opts.authResolution === 'pending') {
    if (opts.authProbeResolved) {
      // Bounded failsafe: prefer public over forever splash
      return opts.hasUser ? 'authenticated' : 'public';
    }
    return 'bootstrap-splash';
  }

  // authenticated
  if (!opts.hasUser) {
    // Defensive: resolution says authenticated but user cleared → public
    return 'public';
  }
  if (opts.authenticatedDataLoading) {
    return 'auth-data-splash';
  }
  return 'authenticated';
}
